// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

// Same origin, /api namespace — Vite proxy in dev, CloudFront /api/* in
// prod. Keeping the API off the SPA's view paths means a full-document
// load of any view URL (back button, reload, bookmark) can never hit a
// JSON route.

// Type-only (erased at build): the WS-H proof object every SIWB v2
// surface carries (HW.6 / COB-326).
import type { AppSignatureProof } from '@cobault/key-agent';

const BASE = '/api';

export interface VersionResponse {
  name: string;
  version: string;
  commit: string;
  /** F6: lower-case bitcoin network name (`bitcoin`, `signet`, `testnet`, `regtest`). */
  bitcoin_network: string;
  /** F12: base64-encoded 32-byte public salt for Argon2id passcode
   *  verifier derivation. Mirrors `PASSCODE_SALT_PUBLIC` server-side. */
  passcode_public_salt_b64: string;
}

export interface FaucetRequest {
  address: string;
  amount_sats: number;
}

export interface FaucetResponse {
  txid: string;
  address: string;
  amount_sats: number;
}

// ── F7 Wallet ────────────────────────────────────────────────────────

export interface WalletRegisterRequest {
  wallet_address: string;
}

export interface WalletRegisterResponse {
  wallet_address: string;
  status: 'registered' | 'already_registered';
}

export interface WalletUtxo {
  txid: string;
  vout: number;
  value: number;
  status: {
    confirmed: boolean;
    block_height?: number;
    block_hash?: string;
    block_time?: number;
  };
}

export interface WalletTx {
  txid: string;
  fee: number;
  status: {
    confirmed: boolean;
    block_height?: number;
    block_hash?: string;
    block_time?: number;
  };
  amount_sats: number;
  direction: 'credit' | 'debit';
  counterparty?: string;
}

export interface WalletBroadcastRequest {
  tx_hex: string;
}

export interface WalletBroadcastResponse {
  txid: string;
}

// ── Fee-rate tier ladder (COB-485) ──────────────────────────────────────

/** The four ladder tier keys, cheapest first. Server-defined — the SPA never
 *  invents a tier, only renders the ones it is served. */
export type FeeTierKey = 'minimum' | 'economy' | 'standard' | 'fast';

/** One served tier. `sat_vb` is already an integer, already clamped into the
 *  deployment's `[floor, ceiling]` policy bounds, and already made monotonic
 *  against its neighbours — the browser applies no arithmetic of its own. */
export interface FeeTier {
  key: FeeTierKey;
  label: string;
  /** Present only on the default tier (`"default"`). The ladder makes no
   *  per-tier time claims — see `FEE_TIME_CAVEAT` in `fee-selector.tsx`. */
  sub_label?: string;
  sat_vb: number;
}

/** Where the served rates came from. `fallback` means esplora had nothing
 *  (regtest, no mempool data, or an outage) and the static ladder is being
 *  served — the UI must say so rather than presenting defaults as estimates. */
export type FeeLadderSource = 'esplora' | 'fallback';

export interface FeeTiersResponse {
  tiers: FeeTier[];
  source: FeeLadderSource;
  floor_sat_vb: number;
  ceiling_sat_vb: number;
  /** Which tier to select by default. Served, not hardcoded client-side. */
  default_tier: FeeTierKey;
  /** Unix seconds the underlying estimates were fetched at (not when the
   *  response was served — the server caches for 30 s). Rendered
   *  browser-local as "rates as of HH:MM:SS". */
  as_of: number;
}

export interface SharedWithdrawalQuoteRequest {
  /** The draft destination set. Only the count affects the size estimate;
   *  addresses are not parsed server-side, so a quote survives a half-typed
   *  address. */
  outputs: SharedWithdrawalOutput[];
}

/** A served tier priced for one specific draft withdrawal. `fee_sats` comes
 *  from the same server code the withdrawal will be built with — it is the
 *  fee, not an approximation of it. */
export interface QuotedFeeTier extends FeeTier {
  fee_sats: number;
}

export interface SharedWithdrawalQuoteResponse {
  est_vsize: number;
  n_inputs: number;
  min_fee_sats: number;
  floor_sat_vb: number;
  ceiling_sat_vb: number;
  source: FeeLadderSource;
  default_tier: FeeTierKey;
  as_of: number;
  tiers: QuotedFeeTier[];
}

export interface CreateVaultRequest {
  user_multisig_pubkey_hex: string;
  user_refund_pubkey_hex: string;
  user_payout_address: string;
  principal_sats: number;
  user_fingerprint?: string;
  derivation_index?: number;
  /** WS-D (COB-285): U and U_refund as key-origin expressions
   *  (`[fingerprint/account'/path']xpub`) so the server's creation-time
   *  equality gate can re-derive each at its pinned index and byte-compare
   *  against the literal descriptor. Optional — omitting them (a legacy
   *  client) skips the U/U_refund half of the gate. */
  user_key?: string;
  user_refund_key?: string;
}

export interface ContractResponse {
  schema_version: number;
  vault_id: string;
  multisig_descriptor: string;
  user_payout_address: string;
  platform_refund_address: string;
  principal_sats: number;
  reward_sats: number;
  term_start_block: number;
  term_end_block: number;
  refund_unlock_block: number;
  max_loss_bps: number;
  loss_formula: string;
  strikes: { short_usd: number; long_usd: number };
  settlement_reference_time_unix: number;
  price_source_id: string;
}

export interface ReceiptResponse {
  schema_version: number;
  contract_hash_hex: string;
  vault_id: string;
  verifier_multisig_pubkey_hex: string;
  created_at_unix: number;
}

export interface SignedReceiptResponse {
  receipt: ReceiptResponse;
  signature_base64: string;
}

export interface CreateVaultResponse {
  vault_id: string;
  state: string;
  contract: ContractResponse;
  contract_hash_hex: string;
  // COB-245: the deposit address is no longer returned at creation — it is
  // withheld until the owner confirms the contract on the independent verifier,
  // then revealed on GET /vault/:id/details. Kept optional (always absent here)
  // so the shared success view degrades to the "confirm on the verifier" step.
  deposit_address?: string;
  verifier_multisig_pubkey_hex: string;
  signed_receipt: SignedReceiptResponse;
  // HW.9 (COB-329): the platform (P) + verifier (V) account xpubs, as
  // key-origin expressions in the network's version bytes, a hardware
  // session drops straight into the on-device wallet-policy ceremony (U /
  // U_refund come from the device itself). Present only for the
  // enclave-held cohort — the only one that reports an xpub and the only
  // one the hardware flow supports; absent for software / Web3Auth vaults.
  cosigner_account_keys?: CosignerAccountKeys;
}

/** HW.9 (COB-329): the platform + verifier account xpubs (key-origin
 *  expressions) a hardware session feeds into the device wallet policy. */
export interface CosignerAccountKeys {
  platform: string;
  verifier: string;
}

/**
 * S05.6: `GET /vault/:id` is now a minimal public response. The full
 * contract, signed receipt, and platform-internal settlement payloads
 * moved to `GET /vault/:id/details` (owner-only, requires auth).
 */
export interface GetVaultResponse {
  vault_id: string;
  state: string;
  contract_hash_hex: string;
  // COB-245: withheld (absent) while the vault is ACCEPTED; present once the
  // vault has funded (the address is on-chain by then).
  deposit_address?: string;
  created_at_unix: number;
  updated_at_unix: number;
  reward_txid: string | null;
  sub_status?: string;
  error_message?: string;
  withdrawal_txid?: string;
  refund_txid?: string;
}

/**
 * Response from `GET /vault/:id/details` — authenticated + owner-only.
 * Superset of `GetVaultResponse` with the full contract and every
 * platform-internal field the SPA needs to render the vault detail
 * page.
 */
export interface GetVaultDetailsResponse {
  vault_id: string;
  state: string;
  /** Confirmation gate the watcher enforces (6 mainnet / 1 regtest).
   *  The SPA renders "N of {required_confirmations} confirmations"
   *  against it; must come from the backend because the bundle is
   *  byte-identical across envs. (Part B.) */
  required_confirmations: number;
  contract: ContractResponse;
  contract_hash_hex: string;
  // COB-245: withheld (absent) until `contract_acknowledged` is true — i.e.
  // until the owner has confirmed the registered contract on the verifier.
  deposit_address?: string;
  /** COB-245 / COB-429: whether the owner has independently acknowledged the
   *  CURRENTLY-registered contract on the verifier. Verifier-reported for
   *  ACCEPTED, FUNDING and FUNDED; `true` for every state after that.
   *  At ACCEPTED, `false` gates the deposit address to absent and the SPA
   *  routes the user to the verifier confirmation step before funding.
   *  Past ACCEPTED, `false` means a reaccept replaced the contract
   *  (`sub_status = 'reapproved_awaiting_ack'`, or `'awaiting_ack'` post-reward
   *  per COB-428) and the vault is held until the owner confirms the new one —
   *  the SPA must surface that confirmation as the vault's next action. */
  contract_acknowledged: boolean;
  verifier_multisig_pubkey_hex: string;
  signed_receipt: SignedReceiptResponse;
  created_at_unix: number;
  updated_at_unix: number;
  reward_txid: string | null;
  derivation_index?: number;
  withdrawal_psbt_hex?: string;
  withdrawal_txid?: string;
  settlement_price_cents?: number;
  user_sats?: number;
  platform_sats?: number;
  settlement_record_json?: unknown;
  /** COB-491 (R5): the verifier's `SignedWithdrawalAttestation` over the exact
   *  transaction in `withdrawal_psbt_hex`. Served here for the **resume** path
   *  — after a reload the review step is re-entered from persisted state with
   *  no initiate response in hand, so an artifact carried only on that response
   *  would be missing exactly when the user is about to sign. Absent for
   *  withdrawals initiated before R5; the key agent is what fails closed on it,
   *  never this type. Opaque here on purpose: the SPA relays it, the agent
   *  parses and verifies it. */
  withdrawal_attestation_json?: unknown;
  sub_status?: string;
  trade_json?: unknown;
  yield_harvest_json?: unknown;
  error_message?: string;
  refund_txid?: string;
  /** COB-396: creation-time counterparty descriptor. The backend persists
   *  the offer's `productco.id` on the vault at creation (immutable —
   *  `reaccept_offer` can replace the offer snapshot, but a Bluejade vault
   *  stays Bluejade-labelled for life) and serves the descriptor here.
   *  Absent = legacy / descriptor-less offer = the stock Cobault-ProductCo
   *  label. Label-only on vault-detail: the custody surface (addresses,
   *  withdrawal, verifier embed) keeps the Cobault frame. */
  productco?: ProductCoDescriptor;
}

export interface VaultTx {
  txid: string;
  type: 'deposit' | 'reward' | 'withdrawal' | 'refund';
  direction: 'credit' | 'debit';
  amount_sats: number | null;
  counterparty: string | null;
  confirmed: boolean;
  /** On-chain confirmation depth (`tip - block_height + 1`); `0` while
   *  unconfirmed. Drives the "N of M confirmations" counter on the
   *  vault detail page during the deposit + reward waits. (Part B.) */
  confirmations: number;
  block_time?: number;
}

export interface InitiateWithdrawalResponse {
  psbt_hex: string;
  settlement_price_cents: number;
  user_sats: number;
  platform_sats: number;
  signed_settlement_record: unknown;
  /** COB-491 (R5): the verifier's `SignedWithdrawalAttestation` over the exact
   *  transaction in `psbt_hex`. Threaded straight into the key agent's
   *  `signPsbt`, which verifies it against the root-signed identity chain and
   *  refuses without it. Absent when the verifier predates R5 — in which case
   *  the sign fails closed, by design (no grandfather path). */
  signed_withdrawal_attestation?: unknown;
}

export interface CompleteWithdrawalRequest {
  signed_psbt_hex: string;
}

export interface CompleteWithdrawalResponse {
  /**
   * Always `"pending"` — the user-signed PSBT has been enqueued for
   * the watcher to co-sign and broadcast. Poll `GET /vault/:id` for
   * `withdrawal_txid` to observe completion.
   */
  status: string;
}

export interface VaultSummary {
  vault_id: string;
  state: string;
  principal_sats: number;
  reward_sats: number;
  // COB-245: withheld (absent) while the vault is ACCEPTED; the funding
  // address is revealed on the per-vault detail endpoint, not in the list.
  deposit_address?: string;
  created_at_unix: number;
  derivation_index: number | null;
  settlement_reference_time_unix: number;
  sub_status?: string;
  error_message?: string;
}

export interface ListVaultsResponse {
  vaults: VaultSummary[];
}

// ── SIWB auth types (T31) ─────────────────────────────────────────────

export interface ChallengeResponse {
  challenge: string;
  expires_at_unix: number;
}

export interface VerifyResponse {
  token: string;
  expires_at_unix: number;
  /** S06.6: opaque refresh-token secret. Client persists this (not
   *  localStorage — in-memory only for SPA lifetime) and presents it
   *  at /auth/refresh to obtain a fresh access+refresh pair. */
  refresh_token: string;
  refresh_expires_at_unix: number;
  /** F3: server-side KYC status. 'not_started' or 'passed'. */
  kyc_status: string;
  /** COB-340: whether the account's email is server-verified. Drives
   *  the verify-email banner and gates verified-email surfaces
   *  (in-app shared-vault accept, COB-337). */
  email_verified: boolean;
}

export interface RefreshResponse {
  token: string;
  expires_at_unix: number;
  refresh_token: string;
  refresh_expires_at_unix: number;
}

// ── KYC (Phase B — real productco-kyc seam) ───────────────────────────

export interface KycStatusResponse {
  kyc_status: string;
  review_answer?: string;
}

export interface KycAccessTokenResponse {
  token: string;
}

// ── Terms & Conditions (COB-260 — ProductCo T&C acceptance gate) ──────

/** `GET /auth/terms/status` — the current ProductCo terms (version +
 *  Markdown body + hash) joined with whether this user has already accepted
 *  that exact version. `accepted_version` is omitted by the server
 *  (`skip_serializing_if`) when the user has never accepted. */
export interface TermsStatusResponse {
  current_version: number;
  accepted_version?: number;
  accepted: boolean;
  body_md: string;
  sha256: string;
  /** COB-394/COB-397: optional sibling-brand descriptor for the
   *  counterparty whose terms these are — served by the ProductCo terms
   *  surface and passed through by the backend. Present → the modal renders
   *  "<DisplayName> Terms & Conditions" + lockup; absent → the exact legacy
   *  heading. */
  productco?: ProductCoDescriptor;
}

/** `POST /auth/terms/accept` response. `version` echoes the version that was
 *  recorded as accepted. */
export interface AcceptTermsResponse {
  accepted: boolean;
  version: number;
}

// ── Residency attestation (COB-549 / GF-4) ────────────────────────────

/** `GET /auth/residency/status` — whether this user has recorded a
 *  self-attested country of residence, read from user-app's local UX copy
 *  (no cross-service call). `jurisdiction` / `attested_at_unix` are non-null
 *  exactly when `attested` is true.
 *
 *  This is NOT a gate: a user who has never attested keeps every servicing
 *  surface. It decides one thing — whether the SPA prompts. */
export interface ResidencyStatusResponse {
  attested: boolean;
  jurisdiction: string | null;
  attested_at_unix: number | null;
}

/** `POST /auth/residency/attest` response — productco-kyc's record, echoed
 *  back. The SPA updates its session state from THIS, not from what it sent,
 *  because the authoritative store is the one upstream. */
export interface AttestResidencyResponse {
  jurisdiction: string;
  attested_at_unix: number;
}

// ── F12: email + passcode onboarding types ────────────────────────────

export type PasscodeCheckStatus = 'no_account' | 'match' | 'mismatch' | 'web3auth';

export interface PasscodeCheckRequest {
  email: string;
  verifier_hex: string;
}

export interface PasscodeCheckResponse {
  status: PasscodeCheckStatus;
}

export interface RegisterRequest {
  email: string;
  verifier_hex: string;
  pubkey: string;
  challenge: string;
  signature: string;
}

export interface RegisterResponse {
  token: string;
  expires_at_unix: number;
  refresh_token: string;
  refresh_expires_at_unix: number;
  kyc_status: string;
  /** COB-340: `false` on a fresh round-trip registration (the SPA shows
   *  "check your email"); `true` when a Web3Auth idToken verified the
   *  email at insert. */
  email_verified: boolean;
}

/** 0034: passwordless registration (Web3Auth + hardware). No
 *  `verifier_hex` — these accounts have no passcode. `email` comes from
 *  the social provider's getUserInfo (Web3Auth) or the login panel
 *  (hardware). HW.6: the SPA always sends the WS-H `proof` object; the
 *  server's legacy bare-`signature` arm stays for deployed bundles.
 *  Response is `RegisterResponse`. */
export interface RegisterWeb3AuthRequest {
  email: string;
  pubkey: string;
  challenge: string;
  proof: AppSignatureProof;
  /** COB-340: where the email came from — `'provider'` (social-login
   *  userinfo), `'web3auth_otp'` (Web3Auth email-OTP), or `'typed'`
   *  (user-entered, e.g. the hardware path). Absent ≡ `'typed'`.
   *  `provider`/`web3auth_otp` must be backed by `web3auth_id_token`. */
  email_source?: 'provider' | 'web3auth_otp' | 'typed';
  /** COB-340: the Web3Auth idToken proving a `provider`/`web3auth_otp`
   *  claim. The server verifies it (JWKS signature, our audience,
   *  expiry, email match) — the claim is never trusted bare. */
  web3auth_id_token?: string;
}

export interface ResetPasscodeRequest {
  email: string;
  new_verifier_hex: string;
  pubkey: string;
  challenge: string;
  signature: string;
}

export interface ResetPasscodeResponse {
  token: string;
  expires_at_unix: number;
  refresh_token: string;
  refresh_expires_at_unix: number;
  kyc_status: string;
  /** COB-340: mirrors `VerifyResponse.email_verified` — the reset flow
   *  signs the user in, so the SPA needs the same session flag. */
  email_verified: boolean;
}

export interface ChangePasscodeRequest {
  new_verifier_hex: string;
}

// ── COB-340: email verification ──────────────────────────────────────

export interface VerifyEmailConfirmRequest {
  /** Raw token from the `/verify-email#token=…` link fragment. */
  token: string;
}

export interface VerifyEmailConfirmResponse {
  /** `'verified'` on a successful flip; `'already_verified'` when the
   *  account was already verified for this email (second outstanding
   *  link, or a replayed click of a consumed one). */
  status: 'verified' | 'already_verified';
}

export interface VerifyEmailResendResponse {
  /** `'ok'` when a fresh link was issued; `'already_verified'` when
   *  there is nothing to do. */
  status: 'ok' | 'already_verified';
}

// ── COB-346: self-service email change (unverified accounts) ─────────

export interface ChangeEmailRequest {
  new_email: string;
}

export interface ChangeEmailResponse {
  /** `'ok'` — the account's email was changed and a fresh verification
   *  link fired to the new address. */
  status: 'ok';
}

export interface EmailVerificationStatusResponse {
  email_verified: boolean;
  /** `'self_roundtrip'` / `'provider'` / `'web3auth_otp'` when
   *  verified, `null` otherwise. */
  source: string | null;
  /** COB-345: the account's email (lower-cased), or `null` for a pre-F12
   *  row that never registered one. The server serializes the `Option`
   *  as an explicit `null` (no `skip_serializing_if`), so the field is
   *  always present. This is the SPA's email-string source for a
   *  provider-less Web3Auth session — it verified on the identity pubkey
   *  alone, so the SDK never handed the client an email to display. */
  email: string | null;
}

// ── F12.A abandon flow ─────────────────────────────────────────────

export interface AbandonRequestBody {
  email: string;
}
export interface AbandonRequestResponse {
  status: 'ok';
}
export interface AbandonConfirmBody {
  token: string;
  email_confirmation: string;
}
export interface AbandonConfirmResponse {
  status: 'abandoned' | 'already_abandoned';
  vault_count: number;
}

// ── Auth token management ─────────────────────────────────────────────

let authToken: string | null = null;
let refreshToken: string | null = null;
let authTokenExpiresAt: number | null = null;

/** Set the JWT access token. Pass null to clear. */
export function setAuthToken(token: string | null): void {
  authToken = token;
  if (token === null) authTokenExpiresAt = null;
}

/** Get the current access token (for session state tracking). */
export function getAuthToken(): string | null {
  return authToken;
}

/** Record the access token's expiry (unix seconds) so `request()` can
 *  refresh proactively before sending, rather than waiting for the
 *  server to 401. Null clears. F12.D — replaces the session-context
 *  timer-driven refresh: activity (API calls) is now what slides the
 *  session, not tab-open wall-clock. */
export function setAuthTokenExpiresAt(expiresAt: number | null): void {
  authTokenExpiresAt = expiresAt;
}

/** Seconds ahead of `exp` at which we preemptively refresh. Long enough
 *  that a normal request's round-trip completes before `exp` even on a
 *  sluggish connection, short enough that idle tabs don't burn refresh
 *  cycles. */
const PROACTIVE_REFRESH_WINDOW_SECS = 30;

/** Set the refresh-token secret. Pass null to clear. S06.6.
 *
 *  In-memory only by design: persisting the refresh token to
 *  localStorage would defeat its purpose — an XSS payload could read
 *  both. Trade-off: a full page reload logs the user out (no remembered
 *  session). Acceptable because (a) the access JWT is short-lived
 *  anyway, (b) unlock-on-reload is a separate flow driven by the
 *  Worker-held seed, and (c) persisted bearer secrets are the exact
 *  shape of leak we want to avoid. */
export function setRefreshToken(token: string | null): void {
  refreshToken = token;
}

export function getRefreshToken(): string | null {
  return refreshToken;
}

// ── F12: 401 notification hook (F8 session-state hygiene) ────────────

let onUnauthorizedCallback: (() => void) | null = null;

/** Register a callback to fire when any API request receives 401 AND
 *  the transparent refresh attempt also failed. The session context
 *  uses this to transition to `sessionStatus = 'expired'` so React
 *  state stays in sync with the cleared token. */
export function setOnUnauthorized(cb: (() => void) | null): void {
  onUnauthorizedCallback = cb;
}

// ── COB-548 (GF-3): 451 geo-restriction hook ─────────────────────────

/** The tier the backend put the caller's country in.
 *
 *  `prohibited` — nothing under `/api` is available.
 *  `servicing`  — only the acquisition surface (register, offer, new
 *                 vault) is refused; existing vaults stay fully usable.
 *
 *  Mirrors the `tier` field of the pinned 451 body in
 *  `services/user-app/src/geo.rs`. */
export type GeoTier = 'prohibited' | 'servicing';

let onGeoRestrictedCallback: ((tier: GeoTier) => void) | null = null;

/** Register a callback to fire when any API request receives 451. The
 *  session context uses this to raise the app-level geo gate.
 *
 *  451 is used by no other error path in this service, so status alone
 *  identifies the case — the body's `tier` then says which of the two
 *  screens to render. Same module-level hook shape as
 *  `setOnUnauthorized`. */
export function setOnGeoRestricted(cb: ((tier: GeoTier) => void) | null): void {
  onGeoRestrictedCallback = cb;
}

// ── Request helper ────────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** COB-548 — a 451 refusal, carrying the tier so a call site can react
 *  locally (e.g. the create-vault button) without going through the
 *  app-level gate. Still an `ApiError`, so every existing
 *  `instanceof ApiError` / `.status` handler keeps working unchanged. */
export class GeoRestrictedError extends ApiError {
  constructor(public readonly tier: GeoTier) {
    super(451, 'geo_restricted');
    this.name = 'GeoRestrictedError';
  }
}

// Single-flight refresh. Multiple concurrent requests that 401 at the
// same time share one /auth/refresh round trip — otherwise the first
// caller's rotation would invalidate every subsequent caller's token
// and cascade everyone to logout.
let refreshInFlight: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (!refreshToken) return false;
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!res.ok) return false;
      const body = (await res.json()) as RefreshResponse;
      authToken = body.token;
      refreshToken = body.refresh_token;
      authTokenExpiresAt = body.expires_at_unix;
      // Session-context subscribes via a listener so it can keep
      // React state in sync (e.g. re-schedule the terminal-expiry
      // timer that anchors on the refresh-token TTL).
      if (onRefreshCallback) {
        try {
          onRefreshCallback(body);
        } catch {
          // Defensive — never let a buggy listener break the API layer.
        }
      }
      return true;
    } catch {
      return false;
    } finally {
      // Release the single-flight lock on the next microtask so
      // simultaneous callers that awaited THIS refresh all see the
      // same resolution before a new refresh can start.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();
  return refreshInFlight;
}

let onRefreshCallback: ((res: RefreshResponse) => void) | null = null;

/** Register a listener for successful transparent refreshes so the
 *  session context can sync React state with the rotated tokens and
 *  re-anchor its terminal-expiry timer. F12.D. */
export function setOnRefresh(cb: ((res: RefreshResponse) => void) | null): void {
  onRefreshCallback = cb;
}

function buildHeaders(init?: RequestInit): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init?.headers as Record<string, string>),
  };
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
  return headers;
}

/** user-app returns `{"error": "<reason>"}` on every non-2xx. Prefer the
 *  parsed reason so callers can render the specific failure instead of
 *  the raw JSON envelope. Falls back to the response text (or a caller-
 *  supplied default) if the body isn't the expected shape. */
async function extractErrorMessage(res: Response, fallback: string): Promise<string> {
  const text = await res.text().catch(() => '');
  if (!text) return fallback;
  try {
    const parsed = JSON.parse(text) as { error?: unknown };
    if (typeof parsed.error === 'string' && parsed.error.length > 0) {
      return parsed.error;
    }
  } catch {
    // Body isn't JSON — use the raw text.
  }
  return text;
}

/** COB-548 — read the `tier` out of a 451 body.
 *
 *  Falls back to `prohibited`, the stricter of the two: an unreadable or
 *  unrecognised body means we cannot prove the user still has access to
 *  their existing vaults, and promising access we can't deliver is the
 *  worse error. */
export async function extractGeoTier(res: Response): Promise<GeoTier> {
  const text = await res.text().catch(() => '');
  try {
    const parsed = JSON.parse(text) as { tier?: unknown };
    if (parsed.tier === 'servicing') return 'servicing';
  } catch {
    // Body isn't JSON — fall through to the strict default.
  }
  return 'prohibited';
}

/** Paths for which proactive pre-send refresh is meaningless (they
 *  don't carry an access token) or would loop (/auth/refresh itself).
 *  Terminal 401 on these is the right outcome. */
function isAuthEndpoint(path: string): boolean {
  return (
    path === '/auth/refresh' ||
    path === '/auth/logout' ||
    path === '/auth/verify' ||
    path === '/auth/challenge' ||
    path === '/auth/register' ||
    path === '/auth/register-web3auth' ||
    path === '/auth/passcode-check' ||
    path === '/auth/verify-email-confirm'
  );
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // F12.D — inactivity-based session timeout. Pre-send: if the access
  // token is within PROACTIVE_REFRESH_WINDOW_SECS of expiry, rotate
  // before we put it on the wire. This is what makes the session
  // activity-driven: idle tabs stop refreshing automatically; API
  // calls are the thing that slides the session forward.
  if (
    authToken !== null &&
    refreshToken !== null &&
    authTokenExpiresAt !== null &&
    !isAuthEndpoint(path)
  ) {
    const now = Math.floor(Date.now() / 1000);
    if (authTokenExpiresAt - now < PROACTIVE_REFRESH_WINDOW_SECS) {
      await tryRefresh();
    }
  }

  let res = await fetch(`${BASE}${path}`, { ...init, headers: buildHeaders(init) });

  // S06.6: 401 on a protected route → try the refresh token once.
  // If refresh succeeds, retry exactly once with the new access token.
  // /auth/refresh itself is excluded (would loop forever on a dead
  // refresh token). /auth/logout and /auth/verify are also excluded —
  // they never carry an access token, so a 401 there is terminal.
  const refreshable =
    res.status === 401 &&
    refreshToken !== null &&
    path !== '/auth/refresh' &&
    path !== '/auth/logout' &&
    path !== '/auth/verify';
  if (refreshable) {
    const ok = await tryRefresh();
    if (ok) {
      res = await fetch(`${BASE}${path}`, { ...init, headers: buildHeaders(init) });
    }
  }

  if (res.status === 401) {
    // Either refresh was not attempted, or it failed, or the retry
    // itself 401'd. Clear local state and notify.
    authToken = null;
    refreshToken = null;
    authTokenExpiresAt = null;
    if (onUnauthorizedCallback) {
      try {
        onUnauthorizedCallback();
      } catch {
        // Defensive — never let a buggy callback break the API layer.
      }
    }
    throw new ApiError(401, await extractErrorMessage(res, 'Session expired'));
  }
  // COB-548 (GF-3): the geofence refusal. Handled before the generic
  // `!res.ok` branch because the body carries a second field (`tier`)
  // that `extractErrorMessage` would discard — and the body may only be
  // read once, so this has to be the branch that reads it.
  if (res.status === 451) {
    const tier = await extractGeoTier(res);
    if (onGeoRestrictedCallback) {
      try {
        onGeoRestrictedCallback(tier);
      } catch {
        // Defensive — never let a buggy callback break the API layer.
      }
    }
    throw new GeoRestrictedError(tier);
  }
  if (!res.ok) {
    throw new ApiError(res.status, await extractErrorMessage(res, 'Unknown error'));
  }
  const text = await res.text();
  return text ? JSON.parse(text) : (undefined as unknown as T);
}

/**
 * COB-394: optional counterparty (ProductCo) brand descriptor. Since
 * COB-395 trading attaches this to its offer response (proxied verbatim by
 * user-app) so the SPA can present the yield product under a distinct
 * sibling brand — identity and copy as *data*, the visual treatment keyed
 * by `id` in the bundle (`brand.ts` / `resolveBrand`). When absent (engine
 * without a configured ProductCo identity, or a rolled-back trading) every
 * offer/terms surface renders the legacy Cobault label with no brand
 * scope. Fields are snake_case per this module's wire convention.
 *
 * HAND-COPIED CROSS-REPO WIRE CONTRACT (TR-D5 class): this interface is
 * the wire source of truth, mirrored by `ProductCoDescriptor` in trading
 * `src/types.rs` (which serves it on `CoverCallOffer.productco`). Any
 * field change must land in lock-step on both sides.
 */
export interface ProductCoDescriptor {
  /** Stable brand id — selects the bundle-side visual treatment. */
  id: string;
  /** Human brand name, e.g. "Bluejade Markets". */
  display_name: string;
  /** Optional endorsement line, e.g. "A Cobault strategy provider". */
  lockup?: string;
}

/** Fields the SPA reads from trading's CoverCallOffer (proxied as-is by user-app). */
export interface OfferResponse {
  id: string;
  days_to_expiry: number;
  spot: number;
  maturity_date: string;
  short_leg: { strike: number; price_btc: number };
  long_leg: { strike: number; price_btc: number };
  net_credit_btc: number;
  max_loss_pct: number;
  offer_limit_btc: number;
  economics: {
    yield_apy: number;
    net_return_on_nominal: number;
    net_return_on_nominal_apy: number;
    min_return_btc: number;
    max_loss_pct: number;
  };
  /** COB-394: optional sibling-brand descriptor for the counterparty on
   *  the other side of this offer. Sent by trading since COB-395 when the
   *  engine has a configured ProductCo identity; absent → legacy Cobault
   *  label (fail-safe). */
  productco?: ProductCoDescriptor;
}

/**
 * COB-386: a legitimate "no offer right now" state. `GET /offer` returns
 * this (with HTTP 200) when the yield engine answered correctly but there
 * is no quotable offer for the moment — trading reported `no_viable_spread`
 * (near-term liquidity too thin for the configured term) or `maintenance`
 * (engine halted / upstream paused). This is NOT an error: it is a normal,
 * self-healing market state that the SPA renders as a neutral card rather
 * than an outage. A real offer never carries an `available` field, so
 * `isOfferUnavailable` discriminates on its presence.
 */
export interface OfferUnavailable {
  available: false;
  /** Stable machine code — must stay in sync with user-app's
   *  `OFFER_UNAVAILABLE_*` constants (`services/user-app/src/lib.rs`). */
  reason: string;
}

/** Either a live offer or a machine-readable "no offer right now" state. */
export type OfferResult = OfferResponse | OfferUnavailable;

/** Narrow an {@link OfferResult} to the "no offer right now" state. */
export function isOfferUnavailable(r: OfferResult): r is OfferUnavailable {
  return (r as Partial<OfferUnavailable>).available === false;
}

// ── Shared Vaults (M2.5, epic COB-186) ───────────────────────────────
// Policy-free m-of-n multisig. Wire shapes mirror user-app's A7 DTOs in
// `services/user-app/src/shared_vault_routes.rs` (the verifier is NOT
// involved in Phase 1). Types are prefixed `Shared*` to avoid collision
// with the single-party yield-vault types above.

export type SharedVaultState =
  | 'PENDING_INVITES'
  | 'PROPOSED'
  | 'ACTIVE'
  | 'EMPTY'
  | 'REJECTED'
  | 'DELETED';

export type SharedParticipantStatus = 'INVITED' | 'ACCEPTED' | 'REJECTED';

/** `CANCELLED` (COB-461) is terminal and reachable from COLLECTING or
 *  FINALIZED: any vault member may call a withdrawal off. It means the platform
 *  will not send the transaction — NOT that an already-signed transaction has
 *  been revoked (those bytes stay valid until their inputs are spent). */
export type SharedPendingTxStatus =
  | 'COLLECTING'
  | 'FINALIZED'
  | 'BROADCAST'
  | 'CANCELLED';

/** COB-357: the persisted outcome of the auto-engaged verifier co-sign request
 *  for a `uses_verifier` withdrawal. A backend watcher engages the verifier and
 *  stores the result. `PENDING` = not yet requested / transient-retrying;
 *  `SIGNED` = the verifier's additive partial is stored; `REFUSED` = the
 *  verifier declined by policy (terminal — it is never re-asked). The verifier
 *  is always additive, so a `REFUSED` never blocks the human quorum (R-INV-1). */
export type VerifierRequestState = 'PENDING' | 'SIGNED' | 'REFUSED';

export interface SharedVaultParticipant {
  slot_id: string;
  /** COB-543: the initiator's OPTIONAL cosmetic name for this slot ("Dad",
   *  "Ops backup") — their own handle on which claim link went to whom.
   *  **Never authz-bearing**: nothing reads a label to decide anything, two
   *  slots may share one, and it is not contract material. A slot is claimed by
   *  presenting its bearer token and nothing else. Render as text only. */
  label: string | null;
  /** COB-543 (D4): the **account** email of the human who holds this slot — the
   *  initiator's own on their slot, and each claimant's once they have
   *  consented. `null` while the slot is still `INVITED`: pre-claim there is no
   *  person, only a link, and the label is all the roster can honestly show.
   *  This is the identity claim members check each other against, and one the
   *  platform can vouch for (the mailbox that account signs in with) rather
   *  than one the initiator typed.
   *
   *  **Display only, and best-effort**: the backend resolves these with one
   *  lookup for the whole roster and renders the roster *without* emails if it
   *  fails. Never key an authorisation or an "is this me" decision on it — use
   *  `is_me` below. */
  member_email: string | null;
  /** The authoritative **"this is my slot"** marker: server-computed, true iff
   *  this row's identity pubkey equals the caller's session identity. Read
   *  straight off the participant row — no join, no lookup, so it can never be
   *  absent or stale.
   *
   *  This, and never `member_email`, is what decides whether the caller holds a
   *  slot: `member_email` is a best-effort DISPLAY field (the backend's roster
   *  read tolerates a failed email lookup by rendering the roster without
   *  emails), so keying "me" on it would let a transient lookup failure make a
   *  member read their own vault as a stranger — no initiator controls, no
   *  acknowledge CTA. Exactly one row can carry `is_me` for a given caller. */
  is_me: boolean;
  status: SharedParticipantStatus;
  is_initiator: boolean;
  acknowledged: boolean;
}

export interface SharedWithdrawalOutput {
  address: string;
  amount_sats: number;
}

export interface SharedPendingTx {
  pending_tx_id: string;
  status: SharedPendingTxStatus;
  outputs: SharedWithdrawalOutput[];
  fee_sats: number;
  /** COB-485: the sat/vB rate the INITIATOR chose, as stored at initiate
   *  (migration 0063). ABSENT on a withdrawal created before that migration —
   *  "not recorded". Never back-derive one from `fee_sats`: the builder charges
   *  `max(ceil(rate × vsize), min_fee_sats)`, so the relay-floor clamp maps
   *  every sub-floor rate onto one fee, which is exactly the 2026-08-05 case a
   *  co-signer needs to be able to see. Display + audit only. */
  fee_rate_sat_vb?: number;
  /** Whether this withdrawal opted into verifier co-signing at initiate (only
   *  possible on a policy vault). Drives the "Request verifier co-signature"
   *  action on a COLLECTING row (B2.1). The verifier is additive — `m` humans
   *  always finalise without it (R-INV-1). */
  uses_verifier: boolean;
  /** COB-352: partials collected so far (humans + the verifier's additive
   *  partial, if it has co-signed). With `quorum_m`, drives the "N of M
   *  signatures" progress label on a COLLECTING row. */
  collected: number;
  /** COB-352: the vault's human signature threshold `m` — the progress-label
   *  denominator. */
  quorum_m: number;
  /** COB-352: whether THIS caller has already contributed a partial to this
   *  withdrawal. The row stays COLLECTING until quorum, so status alone can't
   *  tell a signer they are done; this gates the per-row "Review & sign" button
   *  off once the caller has signed (the re-click was an idempotent no-op — the
   *  reported bug) and swaps in a "you've signed, awaiting co-signers" state.
   *  Backend-authoritative (per-participant), not derived from status. */
  my_signed: boolean;
  /** COB-362: the slot_ids of participants whose contributed key has a stored
   *  partial for this withdrawal — the per-party signing roster the row renders
   *  (each participant shown signed / pending). Backend-authoritative; the
   *  `verifier-shared` sentinel is never a participant slot, so the verifier is
   *  surfaced via `verifier_signed`/`uses_verifier`, never a human slot here. */
  signed_slots: string[];
  /** COB-357: whether the verifier's additive partial is stored for this
   *  withdrawal. `true` ⇒ the verifier has co-signed. */
  verifier_signed: boolean;
  /** COB-357: the persisted verifier-request outcome (auto-engaged by the
   *  watcher). Drives the state-driven verifier tag on the row. */
  verifier_state: VerifierRequestState;
  /** COB-357: the verifier's verbatim refusal reason. ABSENT unless
   *  `verifier_state === 'REFUSED'` (backend `skip_serializing_if`), so it is
   *  optional, not nullable-required. Rendered verbatim — never mapped to
   *  hard-coded allowlist copy. */
  verifier_refusal_reason?: string;
  /** COB-461: when the row reached FINALIZED. ABSENT while COLLECTING (and on a
   *  row cancelled before quorum). Used for one thing: the age heuristic behind
   *  the Cancel button on a FINALIZED row — a healthy one broadcasts within a
   *  watcher poll, so only a row that has sat finalised for a while is worth
   *  offering to cancel. A DISPLAY heuristic; the server applies no age gate. */
  finalized_at_unix?: number;
  /** COB-461: who cancelled this withdrawal (their session identity — NOT
   *  joinable to the roster, which carries emails and slot ids only; see
   *  `pendingTxCancelledLabel`, which deliberately names nobody) and when. Both
   *  ABSENT unless `status === 'CANCELLED'` — the backend pairs them under a
   *  DB CHECK. */
  cancelled_by_identity?: string;
  cancelled_at_unix?: number;
  /** Present once finalized (quorum reached). */
  txid?: string;
}

/** A rolling **authorisation-rate** ceiling (Phase 3 — COB-188/237). Fixed into
 *  the immutable contract at create — a vault has a limit forever or never.
 *  `amount_sats` is a cumulative vault-depletion ceiling (Σ inputs − Σ change,
 *  incl. the miner fee) over the trailing `window_secs`; both are bounded
 *  server-side (`0 < amount_sats ≤ MAX_MONEY`, `window_secs > 0`). Enforcement
 *  lives entirely in verifier-shared. */
export interface SharedVaultRollingLimit {
  amount_sats: number;
  window_secs: number;
}

/** An allowlist spending policy (Phase 2 — COB-214/216). Present ⇒ a *policy*
 *  vault: the seal folds the verifier key `V` into the descriptor and the
 *  verifier co-signs only withdrawals whose outputs are on the allowlist.
 *  Addresses are re-validated + canonicalised (deduped, sorted) server-side via
 *  `cobault-btc-address`, so the client list need only be a best-effort set. A
 *  policy may also carry an optional rolling `limit` (Phase 3 — COB-188/237);
 *  a limit-only policy (no allowlist) is refused by the backend. */
export interface SharedVaultAllowlistPolicy {
  allowlist: string[];
  limit?: SharedVaultRollingLimit;
}

/** The detail `policy` display mirror — the stored canonical policy JSON. `null`
 *  for a no-policy vault. The verifier-shared contract is the source of truth;
 *  this is for display only (A3.1 — COB-237). */
export interface SharedVaultPolicyView {
  allowlist?: string[] | null;
  limit?: SharedVaultRollingLimit | null;
}

/** COB-543: one invitee slot to mint at create.
 *
 *  An object rather than a bare string so the roster stays extensible without a
 *  wire break, and so `{}` — an unlabelled slot — is expressible. `label` is
 *  trimmed at the boundary; empty/whitespace-only becomes `null`; over
 *  {@link SHARED_VAULT_LABEL_MAX_CHARS} characters is a server-side `400`. */
export interface CreateInviteSlot {
  label?: string | null;
}

/** COB-543: the server's cap on a slot label, mirrored client-side as a
 *  `maxLength` so the field stops at the same place the server would 400.
 *  Copied, not shared (`SHARED_VAULT_LABEL_MAX_CHARS` in
 *  `services/user-app/src/shared_vault_routes.rs`) per the estate's rule. */
export const SHARED_VAULT_LABEL_MAX_CHARS = 64;

export interface CreateSharedVaultRequest {
  /** Client-generated UUID — the PoP signature binds it, so it must exist
   *  before the row (see api-contract §2/§3). */
  vault_id: string;
  /** Client-generated UUID for the initiator's own slot. */
  initiator_slot_id: string;
  quorum_m: number;
  /** COB-543: one entry per invitee **slot** to mint (non-empty), in the order
   *  the claim links should be issued. The initiator is not listed. Each entry
   *  mints a slot plus a single-use claim link the response returns; `{}` is a
   *  perfectly good entry (an unlabelled slot). This used to be a list of email
   *  addresses the platform would then mail. */
  invites: CreateInviteSlot[];
  initiator_multisig_pubkey_hex: string;
  initiator_derivation_index: number;
  /** HW.13a: the initiator's account key-origin `[fp/path]xpub` (whose `/0/0`
   *  child equals `initiator_multisig_pubkey_hex`) for the registration record.
   *  Software / Web3Auth send it; the hardware backend omits it until HW.13b. */
  initiator_key_origin?: string;
  /** Legacy PoP: DER-encoded ECDSA over the `cobault-shared-vault-pop-v1`
   *  message, hex. Exactly one of this or `initiator_pop_proof` (WS-H §8). */
  initiator_pop_sig?: string;
  /** WS-H v2 PoP proof object (HW.8) — the device-signable sibling. */
  initiator_pop_proof?: AppSignatureProof;
  initiator_recovery_kit?: unknown;
  /** COB-512 (E1): the Cobault Platform Terms version the initiator accepted on
   *  the confirm screen. REQUIRED — the server refuses an absent version with a
   *  `403` and a superseded one with a `409`, before the vault exists. Import
   *  it from `pages/platform-terms.tsx`; never re-declare the string. */
  platform_terms_version: string;
  /** Optional allowlist policy (Phase 2). Omitted/absent ⇒ a no-policy vault
   *  (the byte-unchanged Phase-1 path). A policy vault requires `quorum_m ≥ 2`
   *  (AR-2). */
  policy?: SharedVaultAllowlistPolicy;
}

/** COB-543: one minted claim link, returned to the initiator.
 *
 *  **`claim_url` is shown exactly once and is never re-displayable.** The raw
 *  bearer token exists client-side only for the life of this response: the
 *  server stores only its SHA-256 (AR-17), so no later read — by the initiator,
 *  by an operator, or by a database dump — can reconstruct the link. An
 *  initiator who loses one regenerates the slot's link
 *  ({@link api.regenerateSharedVaultInvite}), which mints a fresh token and
 *  kills the old one.
 *
 *  It is a **bearer** credential: whoever opens the link can claim that slot, so
 *  each one goes privately to the one person it is for. */
export interface IssuedInvite {
  slot_id: string;
  /** The initiator's cosmetic label for this slot, or `null`. Display only. */
  label: string | null;
  /** `{origin}/shared-vault/accept#token=<raw>` — the token rides in the URL
   *  **fragment**, never a query string (api-contract §2), so it is never sent
   *  to any server and never lands in an access log or a `Referer`. */
  claim_url: string;
  expires_at_unix: number;
}

export interface CreateSharedVaultResponse {
  vault_id: string;
  state: SharedVaultState;
  participants: SharedVaultParticipant[];
  /** COB-543: one per invitee slot, in request order. The **only** appearance
   *  of each raw invite token anywhere — see {@link IssuedInvite}. The
   *  initiator distributes these links themselves; the platform sends nothing. */
  invites: IssuedInvite[];
}

/** One row of `GET /shared-vault` (the caller's shared-vault list). */
export interface SharedVaultSummary {
  vault_id: string;
  /** Cosmetic display label any participant may set/rename (one shared name per
   *  vault). `null`/absent for an unnamed vault — the card then falls back to
   *  the truncated `vault_id`. Never contract material. */
  name?: string | null;
  state: SharedVaultState;
  quorum_m: number;
  n: number;
  has_policy: boolean;
  /** True when the vault is PROPOSED and the caller has not acknowledged,
   *  or a withdrawal is COLLECTING and the caller has not signed — drives
   *  the "pending your signature" pill. */
  my_action_required: boolean;
}

export interface SharedVaultDetailResponse {
  vault_id: string;
  /** Cosmetic display label any participant may set/rename via
   *  `renameSharedVault`. `null`/absent for an unnamed vault — the header then
   *  renders the quorum descriptor as its title. Never contract material. */
  name?: string | null;
  state: SharedVaultState;
  quorum_m: number;
  n: number;
  has_policy: boolean;
  network: string;
  /** The caller's own BIP44 derivation index for this vault, or absent if the
   *  caller has no accepted slot yet. Passed to the key agent as the heal hint
   *  so a missing/stale local vault→index map (common after a re-login) can be
   *  recovered before re-deriving the contributed key (COB-218). */
  my_derivation_index?: number;
  participants: SharedVaultParticipant[];
  /** Display mirror of the stored policy (`{allowlist, limit}`) — `null` for a
   *  no-policy vault; carries the configured rolling limit when set so the SPA
   *  can show the ceiling (A3.1 — COB-237). The verifier-shared contract is the
   *  source of truth; this is for display only. */
  policy?: SharedVaultPolicyView | null;
  /** P2WSH deposit address — present once the vault is PROPOSED or later. */
  deposit_address?: string;
  /** Confirmed + unconfirmed balance at the deposit address, in sats. Absent
   *  until an address is sealed, or if the esplora lookup failed. */
  balance_sats?: number;
  /** The confirmed-only subtotal of `balance_sats` (COB-225) — lets the page
   *  show a confirmed-vs-total split like the wallet. Same absence rules. */
  confirmed_balance_sats?: number;
  pending_tx: SharedPendingTx[];
  /** True iff state === 'EMPTY' (the only delete-eligible state). */
  can_delete: boolean;
}

/** `POST /shared-vault/{id}/name` — set/rename the cosmetic display label.
 *  Echoes the persisted (trimmed) name so the caller can update its view. */
export interface RenameSharedVaultResponse {
  vault_id: string;
  name: string;
}

export interface DeleteSharedInviteResponse {
  removed: boolean;
}

export interface AcceptSharedVaultRequest {
  /** Raw bearer token from the claim-link fragment — body, never URL.
   *  REQUIRED since COB-543: presenting the token is the *only* way to claim a
   *  slot (the tokenless verified-email path, COB-337, is gone with the email
   *  invite model it belonged to). */
  invite_token: string;
  multisig_pubkey_hex: string;
  derivation_index: number;
  /** HW.13a: the accepter's account key-origin `[fp/path]xpub` (whose `/0/0`
   *  child equals `multisig_pubkey_hex`) for the registration record. Software /
   *  Web3Auth send it; the hardware backend omits it until HW.13b. */
  key_origin?: string;
  /** Legacy PoP: DER-encoded ECDSA over the `cobault-shared-vault-pop-v1`
   *  message, hex. Exactly one of this or `pop_proof` (WS-H §8). */
  pop_sig?: string;
  /** WS-H v2 PoP proof object (HW.8) — the device-signable sibling. */
  pop_proof?: AppSignatureProof;
  recovery_kit?: unknown;
  /** COB-512 (E1): the Cobault Platform Terms version this claimant accepted on
   *  the claim-link landing. REQUIRED — the server refuses an absent version
   *  with a `403` and a superseded one with a `409`, and does so *before*
   *  consuming the single-use token, so a refusal never burns the link. */
  platform_terms_version: string;
}

export interface AcceptSharedVaultResponse {
  status: 'ACCEPTED';
  vault_state: SharedVaultState;
  /** COB-338: true when this accept filled the roster but the server-side
   *  seal failed. The accept itself succeeded (the slot is claimed;
   *  `vault_state` stays PENDING_INVITES) and the backend's lazy reconcile
   *  re-runs the seal on subsequent detail reads — render "you're in,
   *  activation is finishing", never an error. */
  activation_pending: boolean;
}

export interface RejectSharedVaultRequest {
  /** Invitees reject by token; an accepted member omits it. */
  invite_token?: string;
}

export interface RejectSharedVaultResponse {
  vault_state: SharedVaultState;
}

/** `GET /shared-vault/{id}/activation` — the sealed two-phase activation
 *  bundle each participant re-derives + acknowledges. */
export interface SharedVaultActivationBundle {
  quorum_m: number;
  /** Participant compressed pubkeys, lowercase hex, byte-sorted (BIP-67). */
  roster: string[];
  network: string;
  multisig_descriptor: string;
  deposit_spk: string;
  deposit_address: string;
  /** Phase 1: always null. */
  verifier_pubkey_hex?: string | null;
  /** Phase 1: always null. */
  contract_hash_hex?: string | null;
  /** HW.13b (COB-333): cosigner account key-origins (`[fp/path]xpub`) the
   *  sealed `sortedmulti` derives from — a Ledger device registers its wallet
   *  policy from these at acknowledge. Absent for a legacy vault with no
   *  registration record; the software / Web3Auth backends ignore it. Passed
   *  straight through to the key-agent on the activation bundle. */
  account_key_origins?: string[];
}

export interface AcknowledgeSharedVaultRequest {
  /** Legacy ack: DER-encoded ECDSA over the
   *  `cobault-shared-vault-activation-v1` manifest, signed with the contributed
   *  multisig key, hex. Exactly one of this or `activation_manifest_proof`. */
  activation_manifest_sig?: string;
  /** WS-H v2 ack proof object (HW.8) — the device-signable sibling. */
  activation_manifest_proof?: AppSignatureProof;
}

export interface AcknowledgeSharedVaultResponse {
  acknowledged: number;
  n: number;
  vault_state: SharedVaultState;
}

export interface InitiateSharedWithdrawalRequest {
  outputs: SharedWithdrawalOutput[];
  /** sat/vB. */
  fee_rate: number;
  /** Phase 1: must be false. */
  uses_verifier?: boolean;
  /** Legacy intent: DER-encoded ECDSA (hex). Opaque to the server (AR-14) —
   *  co-signers' key-agents verify it. Exactly one of this or `intent_proof`
   *  (WS-H §6.6 / §8; HW.8). */
  intent_sig?: string;
  /** WS-H v2 intent proof object (HW.8) — the device-signable sibling. */
  intent_proof?: AppSignatureProof;
  /** COB-485: which ladder tier the rate came from, or omitted for a custom
   *  rate. Observability only — `fee_rate` is the sole authority for what the
   *  withdrawal is built at. */
  fee_tier?: FeeTierKey;
}

export interface InitiateSharedWithdrawalResponse {
  pending_tx_id: string;
  unsigned_psbt_hex: string;
  withdrawal_digest: string;
  /** The stored intent credential, echoed in whichever form was supplied. */
  intent_sig?: string;
  intent_proof?: AppSignatureProof;
}

export interface SharedPartialSigRequest {
  /** The caller's clear-signed partial PSBT, lowercase hex. */
  partial_psbt_hex: string;
}

export interface SharedPartialSigResponse {
  collected: number;
  quorum_m: number;
  state: 'COLLECTING' | 'FINALIZED';
}

export interface SharedWithdrawalStatus {
  status: SharedPendingTxStatus;
  collected: number;
  quorum_m: number;
  /** Compressed pubkeys (hex) that have signed so far. */
  signers: string[];
  outputs: SharedWithdrawalOutput[];
  /** COB-485 (Ticket C): the miner fee this withdrawal pays, in sats — taken
   *  from vault value (AR-3), so it is part of what a co-signer authorises. */
  fee_sats: number;
  /** COB-485 (Ticket C): the rate the initiator chose, in sat/vB. ABSENT on a
   *  pre-0063 withdrawal; see `SharedPendingTx.fee_rate_sat_vb`. */
  fee_rate_sat_vb?: number;
  /** The initiator's intent credential, in exactly one form (WS-H §6.6):
   *  legacy bare DER hex (`intent_sig`) or the v2 proof object
   *  (`intent_proof`). Co-signers dispatch on which is present. */
  intent_sig?: string;
  intent_proof?: AppSignatureProof;
  /** The unsigned base PSBT every co-signer clear-signs + partial-signs. */
  unsigned_psbt_hex: string;
  txid?: string;
}

export interface DeleteSharedVaultResponse {
  state: SharedVaultState;
}

/** `POST /shared-vault/resolve-token` (B4) — public endpoint that maps a
 *  raw invite token to the ids the accept PoP binds. The claim link carries
 *  only the token (URL fragment), so the SPA resolves it before signing.
 *  Token travels in the request BODY, never the URL. */
export interface ResolveInviteTokenRequest {
  invite_token: string;
}

/** What the claim screen needs before an invitee contributes a key (COB-543,
 *  D3). Carries no key material and nothing funds-affecting.
 *
 *  The identity claims are deliberately asymmetric: `inviter_email` is the
 *  initiator's **account** email, which the platform can vouch for, while
 *  `label` is a string the initiator typed — the claimant's own cross-check that
 *  they were handed the right link, never an assertion by us. `quorum_m` and
 *  `participant_count` state the `m`-of-`n` the invitee is joining, so the
 *  commitment is legible before the key is contributed rather than after. */
export interface ResolveInviteTokenResponse {
  vault_id: string;
  slot_id: string;
  state: SharedVaultState;
  inviter_email: string;
  /** The initiator's cosmetic label for this slot, or `null`. Display only —
   *  never authz-bearing; the token is the sole credential. */
  label: string | null;
  quorum_m: number;
  /** The roster size `n` — every non-`REJECTED` slot, including the
   *  initiator's and this one. */
  participant_count: number;
}

/**
 * HW.9 (COB-329): the browser's half of a vault's registration record —
 * the two keys the device controls, U and U_refund, as key-origin
 * expressions (`[fingerprint/account'/path']xpub`, the exact form the
 * on-device wallet policy registered). P and V are written server-side at
 * vault creation, so they are not echoed here; the device-issued policy
 * HMAC stays client-side (device-scoped). Strict endpoint — unknown
 * fields are rejected.
 */
export interface RegisterVaultPolicyRequest {
  user_key: string;
  user_refund_key: string;
  derivation_index: number;
}

export const api = {
  version: () => request<VersionResponse>('/version'),
  getOffer: () => request<OfferResult>('/offer'),
  createVault: (body: CreateVaultRequest) =>
    request<CreateVaultResponse>('/vault', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** HW.9 (COB-329): POST the browser's half of the registration record
   *  (U + U_refund account xpubs) after the on-device ceremony. Strict —
   *  the accept flow now surfaces a failure (retry re-runs it) rather than
   *  swallowing it. */
  registerVaultPolicy: (id: string, body: RegisterVaultPolicyRequest) =>
    request<void>(`/vault/${id}/policy-registration`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getVault: (id: string) => request<GetVaultResponse>(`/vault/${id}`),
  getVaultDetails: (id: string) =>
    request<GetVaultDetailsResponse>(`/vault/${id}/details`),
  getVaultTransactions: (id: string) => request<VaultTx[]>(`/vault/${id}/transactions`),
  /** List vaults for the authenticated user. Identity from JWT, no fingerprint param. */
  listVaults: () => request<ListVaultsResponse>('/vaults'),
  initiateWithdrawal: (id: string) =>
    request<InitiateWithdrawalResponse>(`/vault/${id}/initiate-withdrawal`, { method: 'POST' }),
  completeWithdrawal: (id: string, body: CompleteWithdrawalRequest) =>
    request<CompleteWithdrawalResponse>(`/vault/${id}/complete-withdrawal`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  reacceptOffer: (id: string, offer_json: unknown) =>
    request<void>(`/vault/${id}/reaccept-offer`, {
      method: 'POST',
      body: JSON.stringify({ offer_json }),
    }),
  rejectOffer: (id: string) =>
    request<void>(`/vault/${id}/reject-offer`, { method: 'POST' }),
  // B2: return a deposit / cancel a vault that is still ACCEPTED or
  // FUNDING (pre-trade). The watcher refunds any deposit to the payout
  // address and abandons the vault.
  returnDeposit: (id: string) =>
    request<void>(`/vault/${id}/return`, { method: 'POST' }),

  // ── SIWB auth (T31) ──────────────────────────────────────────────
  getChallenge: (pubkey: string) =>
    request<ChallengeResponse>(`/auth/challenge?pubkey=${encodeURIComponent(pubkey)}`),
  /** Frozen legacy surface (bare DER hex). The SPA login funnel moved
   *  to `verifyChallengeV2` at HW.6; this stays only for anything that
   *  still constructs a bare signature. */
  verifyChallenge: (body: { pubkey: string; challenge: string; signature: string }) =>
    request<VerifyResponse>('/auth/verify', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** HW.6 (COB-326): WS-H proof-object verify — the login surface for
   *  every backend (software emits `der-sha256-v1`, hardware
   *  `bsm-bip137-v1`). */
  verifyChallengeV2: (body: {
    pubkey: string;
    challenge: string;
    proof: AppSignatureProof;
  }) =>
    request<VerifyResponse>('/auth/verify-v2', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  refreshSession: () => {
    if (!refreshToken) {
      return Promise.reject(new ApiError(401, 'no refresh token'));
    }
    return request<RefreshResponse>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  },
  logout: () => {
    if (!refreshToken) return Promise.resolve();
    const body = JSON.stringify({ refresh_token: refreshToken });
    // Fire-and-forget: clear local state immediately, report best-effort
    // to the server. A failed revoke is not a UX blocker — the session
    // is already "over" from the user's perspective.
    const p = request<void>('/auth/logout', { method: 'POST', body }).catch(
      () => undefined,
    );
    authToken = null;
    refreshToken = null;
    return p;
  },

  // ── F12: email + passcode onboarding ─────────────────────────────
  passcodeCheck: (body: PasscodeCheckRequest) =>
    request<PasscodeCheckResponse>('/auth/passcode-check', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  registerEmail: (body: RegisterRequest) =>
    request<RegisterResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** 0034: passwordless registration for a Web3Auth/social-login user. */
  registerWeb3Auth: (body: RegisterWeb3AuthRequest) =>
    request<RegisterResponse>('/auth/register-web3auth', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  resetPasscode: (body: ResetPasscodeRequest) =>
    request<ResetPasscodeResponse>('/auth/reset-passcode', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  changePasscode: (body: ChangePasscodeRequest) =>
    request<void>('/auth/change-passcode', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ── COB-340: email verification ──────────────────────────────────
  /** Public — the emailed single-use token IS the credential. */
  verifyEmailConfirm: (body: VerifyEmailConfirmRequest) =>
    request<VerifyEmailConfirmResponse>('/auth/verify-email-confirm', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** Authenticated, no body — the server reads the caller's own email
   *  off their row. */
  verifyEmailResend: () =>
    request<VerifyEmailResendResponse>('/auth/verify-email-resend', {
      method: 'POST',
    }),
  getEmailVerificationStatus: () =>
    request<EmailVerificationStatusResponse>('/auth/email-verification/status'),
  /** COB-346: authenticated — point an UNVERIFIED account's email at a
   *  new address (typo correction) and fire a fresh verification link.
   *  Passwordless accounts only: the server 409-refuses passcode
   *  (`auth_method='email'`) callers because their Argon2id verifier is
   *  email-derived (session-context gates the affordance via
   *  `changeEmailAvailable`). A normal authenticated call (NOT in
   *  `isAuthEndpoint`), so a 401 transparently refreshes + retries like
   *  any other protected route. */
  changeEmail: (body: ChangeEmailRequest) =>
    request<ChangeEmailResponse>('/auth/change-email', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ── F12.A abandon flow ─────────────────────────────────────────
  abandonRequest: (body: AbandonRequestBody) =>
    request<AbandonRequestResponse>('/auth/abandon-request', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  abandonConfirm: (body: AbandonConfirmBody) =>
    request<AbandonConfirmResponse>('/auth/abandon-confirm', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ── KYC (Phase B) ────────────────────────────────────────────────
  // Poll-on-demand status reconcile: GET flips the user's kyc_status by
  // asking productco-kyc. getKycAccessToken mints the Sumsub WebSDK token
  // the Phase C modal consumes.
  getKycStatus: () =>
    request<KycStatusResponse>('/auth/kyc/status', { method: 'GET' }),
  getKycAccessToken: () =>
    request<KycAccessTokenResponse>('/auth/kyc/access-token', { method: 'POST' }),

  // ── Terms & Conditions (COB-260) ─────────────────────────────────
  // getTermsStatus returns the current ProductCo terms + whether this user
  // has accepted that exact version (the SPA shows the acceptance modal when
  // `accepted` is false). acceptTerms records acceptance of the version the
  // client saw; the server rejects it 409 if a bump landed since load, so a
  // user can never be recorded as accepting an unseen version.
  getTermsStatus: () =>
    request<TermsStatusResponse>('/auth/terms/status', { method: 'GET' }),
  acceptTerms: (version: number) =>
    request<AcceptTermsResponse>('/auth/terms/accept', {
      method: 'POST',
      body: JSON.stringify({ version }),
    }),

  // ── Residency attestation (COB-549 / GF-4) ───────────────────────
  // getResidencyStatus decides whether the residency modal is offered;
  // attestResidency records the user's own assertion (forwarded to
  // productco-kyc's append-only store, which is authoritative). The
  // jurisdiction is sent verbatim — the server refuses anything that is not
  // exactly two uppercase letters rather than coercing, so the recorded
  // value is always the one the user picked.
  getResidencyStatus: () =>
    request<ResidencyStatusResponse>('/auth/residency/status', { method: 'GET' }),
  attestResidency: (jurisdiction: string) =>
    request<AttestResidencyResponse>('/auth/residency/attest', {
      method: 'POST',
      body: JSON.stringify({ jurisdiction }),
    }),

  // ── Test sBTC faucet (F6) ────────────────────────────────────────
  requestFaucet: (body: FaucetRequest) =>
    request<FaucetResponse>('/test/faucet', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ── Wallet (F7) ──────────────────────────────────────────────────
  registerWallet: (body: WalletRegisterRequest) =>
    request<WalletRegisterResponse>('/wallet/register', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getWalletUtxos: () => request<WalletUtxo[]>('/wallet/utxos'),
  getWalletTxs: () => request<WalletTx[]>('/wallet/txs'),

  // ── Fee-rate tier ladder (COB-485) ───────────────────────────────
  /** The served fee ladder — the ONE fee-rate source for every surface, the
   *  wallet send card included (COB-485 Ticket B retired the raw
   *  `/wallet/fee-estimates` proxy this used to sit beside). JWT-gated but
   *  deliberately not wallet-gated, so it also serves a shared-vault-only
   *  user who has never registered a personal wallet. */
  getFeeTiers: () => request<FeeTiersResponse>('/fee-tiers'),
  broadcastWalletTx: (body: WalletBroadcastRequest) =>
    request<WalletBroadcastResponse>('/wallet/broadcast', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ── Shared Vaults (M2.5, epic COB-186) ───────────────────────────
  createSharedVault: (body: CreateSharedVaultRequest) =>
    request<CreateSharedVaultResponse>('/shared-vault', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listSharedVaults: () => request<SharedVaultSummary[]>('/shared-vault'),
  getSharedVault: (id: string) =>
    request<SharedVaultDetailResponse>(`/shared-vault/${id}`),
  /** Set/rename the vault's cosmetic display label — any accepted participant
   *  may rename; the trimmed name is echoed back. Name in the body, never the
   *  URL. */
  renameSharedVault: (id: string, name: string) =>
    request<RenameSharedVaultResponse>(`/shared-vault/${id}/name`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),
  /** On-chain tx history for the vault deposit address — same enriched `WalletTx`
   *  shape as `getWalletTxs`, served by the membership-gated `/txs` route (COB-225). */
  getSharedVaultTxs: (id: string) =>
    request<WalletTx[]>(`/shared-vault/${id}/txs`),
  /** COB-543: **regenerate** an un-accepted slot's claim link — mint a fresh
   *  single-use token, rotate it onto the slot (which kills any prior token for
   *  that slot, AR-17), and return the new claim URL. Nothing is sent; the
   *  initiator distributes the link themselves, exactly as at create. No body:
   *  the slot id is the only handle and it is already in the path.
   *
   *  Initiator-only, `PENDING_INVITES` vault, still-`INVITED` slot — `409`
   *  otherwise, `429` when the mint budget is spent. */
  regenerateSharedVaultInvite: (id: string, slot: string) =>
    request<IssuedInvite>(`/shared-vault/${id}/invite/${slot}`, {
      method: 'POST',
    }),
  deleteSharedVaultInvite: (id: string, slot: string) =>
    request<DeleteSharedInviteResponse>(
      `/shared-vault/${id}/invite/${slot}`,
      { method: 'DELETE' },
    ),
  acceptSharedVault: (id: string, body: AcceptSharedVaultRequest) =>
    request<AcceptSharedVaultResponse>(`/shared-vault/${id}/accept`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  rejectSharedVault: (id: string, body: RejectSharedVaultRequest) =>
    request<RejectSharedVaultResponse>(`/shared-vault/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getSharedVaultActivation: (id: string) =>
    request<SharedVaultActivationBundle>(`/shared-vault/${id}/activation`),
  acknowledgeSharedVault: (id: string, body: AcknowledgeSharedVaultRequest) =>
    request<AcknowledgeSharedVaultResponse>(
      `/shared-vault/${id}/acknowledge`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  /** COB-485: price a draft withdrawal against the current ladder. Read-only
   *  — no PSBT is built, nothing is stored, no withdrawal slot is taken. The
   *  returned `est_vsize` and per-tier `fee_sats` come from the same server
   *  code the withdrawal will be built with, so the browser never computes a
   *  fee itself (the duplicated formula is what COB-479 fixed). */
  quoteSharedWithdrawal: (id: string, body: SharedWithdrawalQuoteRequest) =>
    request<SharedWithdrawalQuoteResponse>(
      `/shared-vault/${id}/withdrawal/quote`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  initiateSharedWithdrawal: (
    id: string,
    body: InitiateSharedWithdrawalRequest,
  ) =>
    request<InitiateSharedWithdrawalResponse>(
      `/shared-vault/${id}/withdrawal`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  submitSharedPartialSig: (
    id: string,
    tx: string,
    body: SharedPartialSigRequest,
  ) =>
    request<SharedPartialSigResponse>(
      `/shared-vault/${id}/withdrawal/${tx}/partial-sig`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  /** COB-461: call off an in-flight withdrawal. Any vault member may; legal
   *  from COLLECTING and FINALIZED, `409` once BROADCAST or already CANCELLED.
   *  Responds with the same shape as `getSharedWithdrawalStatus`, now
   *  `CANCELLED`. Collected partials are retained server-side (audit trail). */
  cancelSharedWithdrawal: (id: string, tx: string) =>
    request<SharedWithdrawalStatus>(
      `/shared-vault/${id}/withdrawal/${tx}/cancel`,
      { method: 'POST' },
    ),
  getSharedWithdrawalStatus: (id: string, tx: string) =>
    request<SharedWithdrawalStatus>(`/shared-vault/${id}/withdrawal/${tx}`),
  deleteSharedVault: (id: string) =>
    request<DeleteSharedVaultResponse>(`/shared-vault/${id}`, {
      method: 'DELETE',
    }),
  /** B4: public token→invite resolve. Token in the body, never the URL. */
  resolveSharedVaultInvite: (body: ResolveInviteTokenRequest) =>
    request<ResolveInviteTokenResponse>('/shared-vault/resolve-token', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};
